import React from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiDollarSign, FiInfo, FiLayers, FiTarget, FiZap } = FiIcons;

const PRESETS = [
  { label: '10 pages', value: 10 },
  { label: '20 pages', value: 20 },
  { label: '50 pages', value: 50 },
  { label: '100 pages', value: 100 },
  { label: '200 pages', value: 200 },
];

// Fixed 3 LLM calls per batch: research (with web search), angle, spintax article
const getCostInfo = () => {
  // Angle call: ~300 input + ~100 output tokens
  const rates = { in: 0.003, out: 0.015 }; // Sonnet 3.5 rates per 1K tokens
  
  const angleCost = ((300 / 1000) * rates.in) + ((100 / 1000) * rates.out);
  // Spintax now also receives the fact sheet (~4000 input tokens) and writes more fields.
  const spintaxCost = ((5000 / 1000) * rates.in) + ((11000 / 1000) * rates.out);
  // Research call: search results are read as input (~25K tokens), ~3K output,
  // plus up to 8 web searches at $10 per 1,000.
  const researchCost = ((25000 / 1000) * rates.in) + ((3000 / 1000) * rates.out) + 8 * 0.01;

  const total = angleCost + spintaxCost + researchCost;

  return {
    estimated: total < 0.01 ? '<$0.01' : `~$${total.toFixed(3)}`,
    totalCalls: 3,
  };
};

const BulkConfig = ({ pageCount, onChange }) => {
  const costInfo = getCostInfo();

  return (
    <div className="p-4 bg-slate-950/60 rounded-lg border border-slate-800 space-y-4">
      <div className="flex items-center gap-2">
        <SafeIcon icon={FiLayers} className="text-indigo-400 text-sm" />
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Bulk Configuration
        </span>
      </div>

      {/* Strategy banner */}
      <div className="p-3 bg-green-950/20 border border-green-800/30 rounded-lg space-y-1.5">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiZap} className="text-green-400 text-sm shrink-0" />
          <p className="text-xs font-semibold text-green-300">Research-First Strategy — Fixed 3 LLM Calls</p>
        </div>
        <p className="text-xs text-slate-400">
          Regardless of page count, only <span className="text-white font-mono">{costInfo.totalCalls}</span> API calls are made. 
          Claude first researches the official site and real user reviews (up to 8 web searches, about $0.01 each), then writes one evaluation template from those facts; each page is assembled with its own structure client-side.
        </p>

        <div className="grid grid-cols-3 gap-2 mt-2">
          <div className="text-center p-1.5 bg-slate-900/60 rounded">
            <p className="text-sm font-bold text-yellow-400">{costInfo.totalCalls}</p>
            <p className="text-xs text-slate-500">LLM Calls</p>
          </div>
          <div className="text-center p-1.5 bg-slate-900/60 rounded">
            <p className="text-sm font-bold text-green-400">{pageCount}</p>
            <p className="text-xs text-slate-500">Pages</p>
          </div>
          <div className="text-center p-1.5 bg-slate-900/60 rounded">
            <p className="text-sm font-bold text-indigo-400">{costInfo.estimated}</p>
            <p className="text-xs text-slate-500">Est. Cost</p>
          </div>
        </div>
      </div>

      {/* Presets */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-2 uppercase tracking-wider">
          Quick Presets
        </label>
        <div className="flex gap-2 flex-wrap">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => onChange(p.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                pageCount === p.value
                  ? 'border-indigo-500 bg-indigo-950/40 text-indigo-300'
                  : 'border-slate-700 text-slate-400 hover:border-slate-600 hover:text-white'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom count */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">
          Custom Count<span className="text-indigo-400 ml-1">*</span>
        </label>
        <input
          type="number"
          min={1}
          max={500}
          value={pageCount}
          onChange={(e) => {
            const v = Math.max(1, Math.min(500, parseInt(e.target.value, 10) || 1));
            onChange(v);
          }}
          className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-2.5 text-white text-sm placeholder-slate-600 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all"
        />
        <p className="text-xs text-slate-600 mt-1">Up to 500 pages — cost stays the same</p>
      </div>

      {/* How it works */}
      <div className="p-3 bg-violet-950/20 border border-violet-800/30 rounded-lg space-y-1.5">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiTarget} className="text-violet-400 text-sm shrink-0" />
          <p className="text-xs font-semibold text-violet-300">How Uniqueness Works</p>
        </div>
        <ol className="text-xs text-slate-400 space-y-1 list-none">
          <li><span className="text-violet-400 font-mono mr-1">1.</span>Your SEO title is used exactly as entered, plus a unique [CODE] per page</li>
          <li><span className="text-violet-400 font-mono mr-1">2.</span>File names use review phrases: keyword-reviews-complaints-6gsua5.html</li>
          <li><span className="text-violet-400 font-mono mr-1">3.</span>LLM generates ONE targeted content angle</li>
          <li><span className="text-violet-400 font-mono mr-1">4.</span>LLM generates ONE spintax article</li>
          <li><span className="text-violet-400 font-mono mr-1">5.</span>Each page resolves differently using a unique seed</li>
        </ol>
      </div>

      {/* Cost note */}
      <div className="flex items-start gap-3 p-3 bg-indigo-950/20 border border-indigo-800/30 rounded-lg">
        <SafeIcon icon={FiDollarSign} className="text-indigo-400 text-sm shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="text-xs font-semibold text-indigo-300">Fixed Cost Regardless of Page Count</p>
          <p className="text-xs text-slate-400">
            Generate <span className="text-white font-mono">10</span> or{' '}
            <span className="text-white font-mono">500</span> pages — the LLM cost is always{' '}
            <span className="text-white font-mono">{costInfo.estimated}</span> for {costInfo.totalCalls} calls.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-2 p-2.5 bg-slate-800/40 rounded-lg">
        <SafeIcon icon={FiInfo} className="text-slate-500 text-xs shrink-0 mt-0.5" />
        <p className="text-xs text-slate-500">
          Every title on each page is your SEO title exactly as entered, plus a unique 8-character [CODE].
          All other content — subtitles, descriptions, card text, video chapters — varies per page via spintax resolution.
        </p>
      </div>
    </div>
  );
};

export default BulkConfig;